import React, { useState } from "react";
import {
  Box,
  IconButton,
  Checkbox,
  Typography,
  Chip,
  Popover,
  MenuItem,
  Menu,
  Avatar,
  AvatarGroup,
  InputBase,
  Tooltip,
  ListItemIcon,
  Divider,
  CircularProgress,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Button,
  FormControlLabel,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import {
  RadioButtonUnchecked,
  RadioButtonChecked,
  Person,
  CalendarToday,
  MoreHoriz,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Search,
  Clear,
  ChatBubbleOutline,
  Edit,
  Delete as DeleteIcon,
  Add,
  LocalOffer,
} from "@mui/icons-material";
import DeleteConfirmModal from "@/Components/Common/DeleteConfirmModal";
import HighestIcon from "../../../assets/icons/highest.svg?react";
import HighIcon from "../../../assets/icons/high.svg?react";
import MediumIcon from "../../../assets/icons/medium.svg?react";
import LowIcon from "../../../assets/icons/low.svg?react";
import { useTranslation } from "react-i18next";
import FolderIcon from "@mui/icons-material/Folder";

import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useGetCompanyMembersQuery } from "@/features/Members/api";
import { toast } from "react-toastify";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  useUpdateBugDetailMutation,
  useGetCommentsByBugIdQuery,
  useDeleteBugMutation,
  useUpdateMultipleBugsMutation,
  useUpdateBugIndexMutation,
  useDeleteMultipleBugsMutation,
  useGetListOfStatusQuery,
} from "@/features/bughurd/api";
import TaskSelectionOverlay from "./TaskSelectionOverlay";
import { colorPalette, typeToIconMapping } from "../utils";
import Pagination from "@/Components/Common/TablePro/Pagination";
import { Icon } from "@iconify/react";
import CommentsPopover from "./CommentsPopover";
import TaskUpdateDrawer from "@/pages/Board/bugherdComponents/TaskUpdateDrawer";

// Redux imports
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import {
  selectBoardState,
  selectColumns,
  selectColumnOrder,
  selectApiData,
  selectFilters,
  selectGroupingConfig,
  selectLoadingStates,
  selectActiveSprintIds,
  selectSearchQuery,
} from "@/features/bughurd/boardSelector";
import { DatePickerPopover } from "./DatePickerPopover";
import moment from "moment";
import {
  setCreateTask,
  setSelectedColumnStatus,
  setSelectedItem,
} from "@/features/bughurd/slice";

export const isCloseToBottom = (element, threshold = 300) => {
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  const windowHeight =
    window.innerHeight || document.documentElement.clientHeight;
  return windowHeight - rect.bottom < threshold;
};
const START_SPRINT_ROW_ID = "start-sprint-row";

interface BoardTableViewProps {
  handlePageChange: (
    columnId: string | null,
    paginationModel: any,
    viewType: string,
    subGroupId?: string
  ) => void;
  refreshColumnData: (columnId?: string, subGroupId?: string) => void;
  isMyTaskView: boolean;
  reactFlow?: any;
}

const BoardTableView: React.FC<BoardTableViewProps> = ({
  handlePageChange,
  refreshColumnData,
  isMyTaskView,
  reactFlow,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const { id } = useParams();
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bugId = searchParams.get("bugId");
  const { pathname } = useLocation();

  // Redux selectors
  const boardState = useAppSelector(selectBoardState);
  const columns = useAppSelector(selectColumns);
  const columnOrder = useAppSelector(selectColumnOrder);
  const apiData = useAppSelector(selectApiData);
  const filters = useAppSelector(selectFilters);
  const groupingConfig = useAppSelector(selectGroupingConfig);
  const loadingStates = useAppSelector(selectLoadingStates);
  const { dateFormat } = useAppSelector((state) => state.appGlobal);
  const allProjectsWithoutJiraConfg =
    boardState?.projects?.filter((p) => p?.jiraConfig?.length === 0) || [];
  const allTags = apiData?.tagList || [];

  // Destructure from Redux state
  const { listOfBugs, statusList, tagList, companyMemberData, listCount } =
    apiData;

  const { groupBy, sortOrder } = groupingConfig;
  const { isBugsLoading, loadingStatuses } = loadingStates;

  const { pagination, projectDetails } = boardState;
  const boardType = projectDetails?.boardType;
  const activeSprintIds = useAppSelector(selectActiveSprintIds);
  const globalSearchQuery = useAppSelector(selectSearchQuery);
  const {
    selectedStatuses,
    selectedAvatars,
    selectedPriorities,
    selectedTags,
    selectedProjects,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
    isRecurring,
    selectedCreatedBy,
  } = filters;

  // Check if any filters are active
  const hasFilters =
    (globalSearchQuery && globalSearchQuery.trim().length > 0) ||
    selectedAvatars?.length > 0 ||
    selectedTags?.length > 0 ||
    selectedStatuses?.length > 0 ||
    selectedPriorities?.length > 0 ||
    selectedProjects?.length > 0 ||
    selectedCreatedBy?.length > 0 ||
    dueDateStart ||
    dueDateEnd ||
    startDateStart ||
    startDateEnd ||
    updatedAtStart ||
    updatedAtEnd ||
    isRecurring;

  // State variables
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [assigneeAnchorEl, setAssigneeAnchorEl] = useState(null);
  const [priorityAnchorEl, setPriorityAnchorEl] = useState(null);
  const [tagsAnchorEl, setTagsAnchorEl] = useState(null);
  const [currentTask, setCurrentTask] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [tagSearchQuery, setTagSearchQuery] = useState("");
  const [tempAssignees, setTempAssignees] = useState([]);
  const [tempTags, setTempTags] = useState([]);
  const [hasAssigneeChanges, setHasAssigneeChanges] = useState(false);
  const [hasTagChanges, setHasTagChanges] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [isBulkOperation, setIsBulkOperation] = useState(false);
  const [showAbove, setShowAbove] = useState(false);

  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [actionTaskId, setActionTaskId] = useState(null);

  // Delete state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  // Comments state
  const [commentsAnchorEl, setCommentsAnchorEl] = useState(null);
  const [commentTaskId, setCommentTaskId] = useState(null);
  const [currentTaskName, setCurrentTaskName] = useState("");

  // Sorting state
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("asc");

  // Expanded groups state
  const [expandedGroups, setExpandedGroups] = useState({});
  const [projectAnchorEl, setProjectAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [projectSearchQuery, setProjectSearchQuery] = useState("");

  const [selectedProjectForStatus, setSelectedProjectForStatus] = useState<
    string | null
  >(null);
  const [statusAnchorElForProject, setStatusAnchorElForProject] =
    useState<null | HTMLElement>(null);

  // API hooks
  const availableStatus = statusList || [];
  const availableTags = tagList || [];
  const { data: users, isLoading: isLoadingUsers } =
    useGetCompanyMembersQuery(1);
  const [updateBug, { isLoading: isUpdating }] = useUpdateBugDetailMutation();
  const [deleteBug, { isLoading: isDeleting }] = useDeleteBugMutation();
  const [deleteMultipleBugs, { isLoading: isDeletingMultiple }] =
    useDeleteMultipleBugsMutation();
  const [updateMultipleBugs, { isLoading: isUpdatingMultipleBugs }] =
    useUpdateMultipleBugsMutation();
  const [updateBugIndex] = useUpdateBugIndexMutation();

  const {
    data: statusListData,
    isLoading: isStatusLoading,
    isFetching: isStatusFetching,
  } = useGetListOfStatusQuery(
    { projectId: selectedProjectForStatus },
    {
      skip: !selectedProjectForStatus,
      refetchOnMountOrArgChange: true,
    }
  );

  const projectStatuses = statusListData?.data?.decryptedData?.items || [];

  // Fetch comments
  const { data: commentsData, isLoading: isLoadingComments } =
    useGetCommentsByBugIdQuery(
      { bugId: commentTaskId },
      { skip: !commentTaskId, refetchOnMountOrArgChange: true }
    );

  const comments = commentsData?.data?.decryptedData?.items || [];

  const handleGoToBacklog = () => {
    dispatch(setSelectedItem("Backlog"));
    navigate(`/defect-tracker/projects/${id}/backlog`);
  };

  const handleProjectClick = (
    event: React.MouseEvent<HTMLElement>,
    projectId: string
  ) => {
    event.stopPropagation();
    setSelectedProjectForStatus(projectId);
    setStatusAnchorElForProject(event.currentTarget);
  };

  // Add this handler after the bulk tags handlers
  const handleBulkProjectChange = (anchorEl: HTMLElement) => {
    setIsBulkOperation(true);
    setProjectSearchQuery("");
    setProjectAnchorEl(anchorEl);
  };

  const applyBulkProjectChange = async (
    projectId: string,
    statusId: string
  ) => {
    if (!selectedTasks.length) return;

    try {
      const items = selectedTasks.map((taskId) => {
        const task = findTask(taskId);
        return {
          _id: taskId,
          project: projectId,
          bugStatus: statusId,
          type: task?.type || "BUG",
        };
      });

      await updateMultipleBugs({ items, sourceProject: id }).unwrap();

      toast.success(t("Project updated for all selected tasks"));
      setSelectedTasks([]);
      refreshData();
      refreshColumnData();
    } catch (error: any) {
      console.error("Error updating projects:", error);
      toast.error(
        t("Failed to update projects") +
          ": " +
          (error.data?.message || t("Unknown error"))
      );
    } finally {
      setProjectAnchorEl(null);
      setStatusAnchorElForProject(null);
      setSelectedProjectForStatus(null);
      setIsBulkOperation(false);
      setProjectSearchQuery("");
    }
  };

  // Convert task data to row format
  const convertTasksToRows = (tasks) => {
    return tasks.map((task) => ({
      id: task._id,
      _id: task._id,
      summary: task.summary || "",
      assignTo: task.assignTo || [],
      bugStatus: task.bugStatus || "",
      dueDate: task.dueDate || "",
      severity: task.severity || "",
      commentCount: task.commentCount || 0,
      type: task.type || "",
      bugKey: task.bugKey || "",
      createdBy: task.createdBy || {},
      createdAt: task.createdAt || "",
      updatedAt: task.updatedAt || "",

      tags: Array.isArray(task.tags)
        ? task.tags
            .map((tag) => {
              if (typeof tag === "string") {
                const foundTag = availableTags.find((t) => t.tagName === tag);
                return foundTag;
              }

              return tag;
            })
            .filter((tag) => tag && (tag._id || tag.tagName))
        : [],
    }));
  };

  // Get data for non-grouped view
  const globalRowsData =
    groupBy === "None" ? convertTasksToRows(listOfBugs || []) : [];
  const globalTotalRows = groupBy === "None" ? listCount || 0 : 0;

  // Handle group expand/collapse
  const handleGroupToggle = (groupId) => {
    const isExpanding = !expandedGroups[groupId];

    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: isExpanding,
    }));
  };

  // Handle pagination for specific group
  const handleGroupPageChange = (groupId, newPaginationModel) => {
    handlePageChange(groupId, newPaginationModel, "table");
  };

  // Handle pagination for non-grouped view
  const handleGlobalPaginationChange = (newPaginationModel) => {
    const updatedModel = {
      ...newPaginationModel,
      page: Math.max(1, newPaginationModel.page),
    };

    // For non-grouped view, use null as groupId
    handlePageChange(null, updatedModel, "table");
  };

  // Handle sorting
  const handleSort = (field) => {
    const isAsc = sortBy === field && sortDirection === "asc";
    setSortBy(field);
    setSortDirection(isAsc ? "desc" : "asc");

    // If grouped, refresh all expanded groups with new sorting
    if (groupBy !== "None") {
      Object.keys(expandedGroups).forEach((groupId) => {
        if (expandedGroups[groupId]) {
          const groupPagination = pagination[groupId] || { page: 1, limit: 25 };
          handlePageChange(
            groupId,
            {
              ...groupPagination,
              sortBy: field,
              sortDirection: isAsc ? "desc" : "asc",
            },
            "table"
          );
        }
      });
    } else {
      // For non-grouped view
      const currentPagination = pagination["global"] || { page: 1, limit: 25 };
      handlePageChange(
        null,
        {
          ...currentPagination,
          sortBy: field,
          sortDirection: isAsc ? "desc" : "asc",
        },
        "table"
      );
    }
  };

  const StatusIcon = ({ iconName, size = 24, type = "" }) => {
    if (!iconName) return null;

    return (
      <Icon
        icon={iconName}
        width={size}
        height={size}
        style={{
          color:
            theme.palette.mode === "dark"
              ? theme.palette.background.paper
              : "text.primary",
          filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
          marginLeft: type === "group" ? "0" : "5px",
        }}
        onError={() => {
          console.warn(
            t("Failed to load status icon: tags not working", { iconName })
          );
        }}
      />
    );
  };

  // Find task across all data
  const findTask = (taskId) => {
    if (groupBy === "None") {
      return globalRowsData.find((task) => task._id === taskId);
    } else {
      // Search in grouped data from columns
      for (const groupId of Object.keys(columns)) {
        const tasks = columns[groupId]?.tasks || [];
        const task = tasks.find((t) => t._id === taskId);
        if (task) return convertTasksToRows([task])[0];
      }
    }
    return null;
  };

  // Task selection handlers
  const handleSelectionModelChange = (newSelectionModel) => {
    setSelectedTasks(newSelectionModel);
  };

  const handleClearSelection = () => {
    setSelectedTasks([]);
  };

  // Process row update
  const processRowUpdate = async (newRow, oldRow) => {
    try {
      if (newRow.summary !== oldRow.summary) {
        await updateBug({
          bugId: newRow.id,
          summary: newRow.summary,
        }).unwrap();
        toast.success(t("Task updated successfully"));
      }
      return newRow;
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error(t("Failed to update task name"));
      return oldRow;
    }
  };

  // Action menu handlers
  const handleActionMenuOpen = (event, taskId) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setActionTaskId(taskId);
  };

  const handleActionMenuClose = () => {
    setMenuAnchorEl(null);
    setActionTaskId(null);
  };

  // Edit and delete handlers
  const handleEditTask = (taskId) => {
    handleActionMenuClose();
    setSelectedTaskId(taskId);
    setIsTaskModalOpen(true);
    navigate(pathname + "?bugId=" + taskId);
  };

  const openDeleteModal = (taskId) => {
    handleActionMenuClose();
    setTaskToDelete(taskId);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setTaskToDelete(null);
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;

    try {
      if (Array.isArray(taskToDelete)) {
        await deleteMultipleBugs({ bugIds: taskToDelete }).unwrap();
        toast.success(t("Tasks deleted successfully"));
        closeDeleteModal();
        return;
      } else {
        await deleteBug(taskToDelete).unwrap();
        toast.success(t("Task deleted successfully"));

        // Refresh data after deletion
        refreshData();
      }
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error(
        t("Failed to delete task") +
          ": " +
          (error.data?.message || t("Unknown error"))
      );
      closeDeleteModal();
    }
  };

  const handleBulkDelete = () => {
    if (selectedTasks.length > 0) {
      setIsDeleteModalOpen(true);
      setTaskToDelete(selectedTasks);
    }
  };

  const handleCloseTaskModal = () => {
    setIsTaskModalOpen(false);
    setSelectedTaskId(null);
    if (bugId) {
      navigate(pathname);
    }
  };

  // Comments handlers
  const handleViewComments = (event, taskId, taskName) => {
    event.stopPropagation();
    setCommentTaskId(taskId);
    setCurrentTaskName(taskName);
    setCommentsAnchorEl(event.currentTarget);
  };

  const handleCloseComments = () => {
    setCommentsAnchorEl(null);
    setCommentTaskId(null);
    setCurrentTaskName("");
  };

  // Click handlers for fields
  const handleStatusClick = (event, task) => {
    event.stopPropagation();
    const isNearBottom = isCloseToBottom(event.currentTarget);
    setCurrentTask(task);
    setIsBulkOperation(false);
    setShowAbove(isNearBottom);
    setStatusAnchorEl(event.currentTarget);
  };

  const handlePriorityClick = (event, task) => {
    event.stopPropagation();
    const isNearBottom = isCloseToBottom(event.currentTarget);
    setCurrentTask(task);
    setIsBulkOperation(false);
    setShowAbove(isNearBottom);
    setPriorityAnchorEl(event.currentTarget);
  };

  const handleAssigneeClick = (event, task) => {
    event.stopPropagation();
    const isNearBottom = isCloseToBottom(event.currentTarget);
    setCurrentTask(task);
    setTempAssignees(task.assignTo || []);
    setHasAssigneeChanges(false);
    setIsBulkOperation(false);
    setShowAbove(isNearBottom);
    setAssigneeAnchorEl(event.currentTarget);
  };

  const handleDateClick = (event, task) => {
    event.stopPropagation();
    const isNearBottom = isCloseToBottom(event.currentTarget);
    setCurrentTask(task);
    setIsBulkOperation(false);
    setShowAbove(isNearBottom);
    setDateAnchorEl(event.currentTarget);
  };

  const handleTagsClick = (event, task) => {
    event.stopPropagation();
    const isNearBottom = isCloseToBottom(event.currentTarget);
    setCurrentTask(task);
    // Ensure tags are properly filtered
    const validTags = Array.isArray(task.tags)
      ? task.tags.filter((tag) => tag && tag._id && tag.tagName)
      : [];
    setTempTags(validTags);
    setHasTagChanges(false);
    setIsBulkOperation(false);
    setShowAbove(isNearBottom);
    setTagsAnchorEl(event.currentTarget);
  };

  // Refresh data helper
  const refreshData = () => {
    if (groupBy === "None") {
      const currentPagination = pagination["global"] || {
        page: 1,
        limit: 25,
      };
      handlePageChange(null, currentPagination, "table");
    } else {
      Object.keys(expandedGroups).forEach((groupId) => {
        if (expandedGroups[groupId]) {
          const groupPagination = pagination[groupId] || {
            page: 1,
            limit: 25,
          };
          handlePageChange(groupId, groupPagination, "table");
        }
      });
    }
  };

  // Field update handlers
  const handleStatusChange = async (newStatus) => {
    if (!currentTask) return;

    try {
      const statusObj = availableStatus.find(
        (status) => status.status === newStatus
      );
      if (!statusObj) {
        toast.error(t("Invalid status selected"));
        return;
      }

      // Store the previous bug status to compare for changes
      const prevBugStatus = currentTask.bugStatus?._id || currentTask.bugStatus;

      await updateBug({
        bugId: currentTask._id,
        bugStatus: statusObj._id,
      }).unwrap();

      // Call updateBugIndex if bugStatus changed
      if (statusObj._id !== prevBugStatus) {
        try {
          await updateBugIndex({
            // crossColumn: "true",
            // items: {
            //   _id: currentTask._id,
            //   boardIndex: 0,
            //   bugStatus: statusObj._id,
            //   project: id, // id from useParams
            // },
            lexo: true,

            project: id,
            nextId: columns[statusObj._id]?.tasks?.[0]?._id || null,
            prevId: null,
            movedId: currentTask._id,
          }).unwrap();
        } catch (error) {
          console.error("Error updating bug index:", error);
        }
      }

      toast.success(t("Status updated successfully"));
      refreshData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(t("Failed to update status"));
    } finally {
      setStatusAnchorEl(null);
      setShowAbove(false);
    }
  };

  const handlePriorityChange = async (newPriority) => {
    if (!currentTask) return;

    try {
      await updateBug({
        bugId: currentTask._id,
        severity: newPriority,
      }).unwrap();

      toast.success(t("Priority updated successfully"));
      refreshData();
    } catch (error) {
      console.error("Error updating priority:", error);
      toast.error(t("Failed to update priority"));
    } finally {
      setPriorityAnchorEl(null);
      setShowAbove(false);
    }
  };

  const handleDateSelect = async (dateString) => {
    if (!currentTask) return;

    try {
      await updateBug({
        bugId: currentTask._id,
        dueDate: dateString,
      }).unwrap();

      toast.success(t("Due date updated successfully"));
      refreshData();
    } catch (error) {
      console.error("Error updating due date:", error);
      toast.error(t("Failed to update due date"));
    } finally {
      setDateAnchorEl(null);
      setShowAbove(false);
    }
  };

  // Assignee handlers
  const handleTempAssigneeChange = (userId) => {
    const userToToggle = users?.data?.decryptedData?.find(
      (u) => u._id === userId
    );
    if (!userToToggle) return;

    if (isBulkOperation) {
      const isUserAssigned = tempAssignees.some((u) => u._id === userId);

      if (isUserAssigned) {
        setTempAssignees((prev) => prev.filter((u) => u._id !== userId));
      } else {
        setTempAssignees((prev) => [
          ...prev,
          { _id: userId, fullName: userToToggle.fullName },
        ]);
      }

      setHasAssigneeChanges(true);
    } else if (currentTask) {
      const userIndex = tempAssignees.findIndex((u) => u._id === userId);

      if (userIndex >= 0) {
        setTempAssignees((prev) => prev.filter((u) => u._id !== userId));
      } else {
        setTempAssignees((prev) => [
          ...prev,
          { _id: userId, fullName: userToToggle.fullName },
        ]);
      }

      setHasAssigneeChanges(true);
    }
  };

  const handleAssigneePopoverClose = async () => {
    setAssigneeAnchorEl(null);
    setShowAbove(false);

    if (hasAssigneeChanges) {
      try {
        if (isBulkOperation) {
          const assigneeIds = tempAssignees.map((u) => u._id);
          const items = selectedTasks.map((taskId) => ({
            _id: taskId,
            assignTo: assigneeIds,
          }));

          await updateMultipleBugs({ items }).unwrap();
          toast.success(t("Assignees updated for all selected tasks"));
          refreshColumnData();
        } else if (currentTask) {
          await updateBug({
            bugId: currentTask._id,
            assignTo: tempAssignees.map((u) => u._id),
          }).unwrap();
          toast.success(t("Assignees updated successfully"));
        }

        refreshData();
      } catch (error) {
        console.error("Error updating assignees:", error);
        toast.error(t("Failed to update assignees"));
      }
    }

    setCurrentTask(null);
    setTempAssignees([]);
    setHasAssigneeChanges(false);
    setIsBulkOperation(false);
  };

  // Tags handlers
  const handleTempTagChange = (tagId) => {
    const tagToToggle = availableTags.find((tag) => tag._id === tagId);
    if (!tagToToggle) return;

    const isTagSelected = tempTags.some((tag) => tag._id === tagId);

    if (isTagSelected) {
      setTempTags((prev) => prev.filter((tag) => tag._id !== tagId));
    } else {
      setTempTags((prev) => [
        ...prev,
        { _id: tagId, tagName: tagToToggle.tagName, color: tagToToggle.color },
      ]);
    }

    setHasTagChanges(true);
  };

  const handleTagsPopoverClose = async () => {
    setTagsAnchorEl(null);
    setShowAbove(false);

    if (hasTagChanges) {
      try {
        if (isBulkOperation) {
          const tagNames = tempTags.map((tag) => tag.tagName);
          const items = selectedTasks.map((taskId) => ({
            _id: taskId,
            tags: tagNames,
          }));

          await updateMultipleBugs({ items }).unwrap();
          toast.success(t("Tags updated for all selected tasks"));
          refreshColumnData();
        } else if (currentTask) {
          const tagNames = tempTags.map((tag) => tag.tagName);

          await updateBug({
            bugId: currentTask._id,
            tags: tagNames,
          }).unwrap();
          toast.success(t("Tags updated successfully"));
        }

        refreshData();
        if (isBulkOperation) {
          setSelectedTasks([]);
        }
      } catch (error) {
        console.error("Error updating tags:", error);
        toast.error(t("Failed to update tags"));
      }
      setCurrentTask(null);
      setTempTags([]);
      setHasTagChanges(false);
      setIsBulkOperation(false);
      setTagSearchQuery("");
    }

    setCurrentTask(null);
    setTempTags([]);
    setHasTagChanges(false);
    setIsBulkOperation(false);
    setTagSearchQuery("");
  };

  // Bulk operation handlers
  const handleBulkPriorityChange = (anchorEl) => {
    setIsBulkOperation(true);
    setPriorityAnchorEl(anchorEl);
  };

  const applyBulkPriorityChange = async (newPriority) => {
    if (!selectedTasks.length) return;

    try {
      const items = selectedTasks.map((taskId) => ({
        _id: taskId,
        severity: newPriority,
      }));

      await updateMultipleBugs({ items }).unwrap();
      toast.success(t("Priority updated for all selected tasks"));
      refreshData();
      setSelectedTasks([]);
      refreshColumnData();
    } catch (error) {
      console.error("Error updating priorities:", error);
      toast.error(
        t("Failed to update priorities") +
          ": " +
          (error.data?.message || t("Unknown error"))
      );
    } finally {
      setPriorityAnchorEl(null);
      setIsBulkOperation(false);
      setShowAbove(false);
    }
  };

  const handleBulkStatusChange = (anchorEl) => {
    setIsBulkOperation(true);
    setStatusAnchorEl(anchorEl);
  };

  const applyBulkStatusChange = async (newStatus) => {
    if (!selectedTasks.length) return;

    try {
      const statusObj = availableStatus.find(
        (status) => status.status === newStatus
      );
      if (!statusObj) {
        toast.error(t("Invalid status selected"));
        return;
      }

      const items = selectedTasks.map((taskId) => ({
        _id: taskId,
        bugStatus: statusObj._id,
      }));

      await updateMultipleBugs({ items }).unwrap();
      refreshColumnData();
      // Call updateBugIndex for each task that changed status
      for (const taskId of selectedTasks) {
        const task = findTask(taskId);
        const prevBugStatus = task?.bugStatus?._id || task?.bugStatus;

        if (statusObj._id !== prevBugStatus) {
          try {
            await updateBugIndex({
              // crossColumn: "true",
              // items: {
              //   _id: taskId,
              //   boardIndex: 0,
              //   bugStatus: statusObj._id,
              //   project: id, // id from useParams
              // },
              lexo: true,

              project: id,
              nextId: columns[statusObj._id]?.tasks?.[0]?._id || null,
              prevId: null,
              movedId: taskId,
            }).unwrap();
          } catch (error) {
            console.error("Error updating bug index for task:", taskId, error);
          }
        }
      }

      toast.success(t("Status updated for all selected tasks"));
      refreshData();
    } catch (error) {
      console.error("Error updating statuses:", error);
      toast.error(
        t("Failed to update statuses") +
          ": " +
          (error.data?.message || t("Unknown error"))
      );
    } finally {
      setStatusAnchorEl(null);
      setIsBulkOperation(false);
      setShowAbove(false);
    }
  };

  const handleBulkAssigneesChange = (anchorEl) => {
    setIsBulkOperation(true);
    setTempAssignees([]);
    setHasAssigneeChanges(false);
    setAssigneeAnchorEl(anchorEl);
  };

  const handleBulkDatesChange = (anchorEl) => {
    setIsBulkOperation(true);
    setDateAnchorEl(anchorEl);
  };

  const applyBulkDateChange = async (dateString) => {
    if (!selectedTasks.length) return;

    try {
      const items = selectedTasks.map((taskId) => ({
        _id: taskId,
        dueDate: dateString,
      }));

      await updateMultipleBugs({ items }).unwrap();
      toast.success(t("Due dates updated for all selected tasks"));
      refreshData();
      refreshColumnData();
    } catch (error) {
      console.error("Error updating due dates:", error);
      toast.error(
        t("Failed to update due dates") +
          ": " +
          (error.data?.message || t("Unknown error"))
      );
    } finally {
      setDateAnchorEl(null);
      setIsBulkOperation(false);
      setShowAbove(false);
    }
  };

  const handleBulkTagsChange = (anchorEl) => {
    setIsBulkOperation(true);
    setTempTags([]);
    setHasTagChanges(false);
    setTagSearchQuery("");
    setTagsAnchorEl(anchorEl);
  };

  const getRowsAsGrouped = (column: any) => {
    if (groupBy === "Status") {
      if (filters.selectedStatuses.length > 0) {
        return filters.selectedStatuses.includes(column?.id);
      } else {
        return true;
      }
    } else if (groupBy === "Assignee") {
      if (filters.selectedAvatars.length > 0) {
        return filters.selectedAvatars.includes(column?.id);
      } else {
        return (
          column?.tasks?.length > 0 ||
          loadingStates.loadingStatuses?.[column?.id]?.isInitialLoading
        );
      }
    } else if (groupBy === "Priority") {
      if (filters.selectedPriorities.length > 0) {
        return filters.selectedPriorities.includes(column?.title);
      } else {
        return true;
      }
    } else if (groupBy === "Tags") {
      if (filters.selectedTags.length > 0) {
        return filters.selectedTags.includes(column?.title);
      } else {
        return true;
      }
    } else {
      return true;
    }
  };

  // Render functions
  const renderStatus = (status, task) => {
    if (!status) return null;

    const statusColumn = Object.values(availableStatus).find(
      (column) => column?.status === status
    );

    const color = statusColumn?.color || "#3399ff";
    const statusIcon = statusColumn?.icon;

    if (status === "IN PROGRESS") {
      return (
        <Chip
          icon={
            statusIcon ? (
              <StatusIcon iconName={statusIcon} size={16} />
            ) : (
              <RadioButtonChecked sx={{ fontSize: 16, color: "white" }} />
            )
          }
          label={status?.status}
          size="small"
          sx={{
            backgroundColor: color,
            color: "black",
            borderRadius: "16px",
            cursor: "pointer",
          }}
          onClick={(event) => handleStatusClick(event, task)}
        />
      );
    } else if (status === "TO DO" || status === "TODO") {
      return (
        <Chip
          icon={
            statusIcon ? (
              <StatusIcon iconName={statusIcon} size={16} />
            ) : (
              <RadioButtonUnchecked sx={{ fontSize: 16, color: "white" }} />
            )
          }
          label={status?.status}
          size="small"
          sx={{
            backgroundColor: color,
            color: "black",
            borderRadius: "16px",
            cursor: "pointer",
          }}
          onClick={(event) => handleStatusClick(event, task)}
        />
      );
    } else {
      return (
        <Chip
          icon={
            statusIcon ? <StatusIcon iconName={statusIcon} size={16} /> : null
          }
          label={status?.status}
          size="small"
          sx={{
            backgroundColor: color,
            color: "black",
            borderRadius: "16px",
            cursor: "pointer",
          }}
          onClick={(event) => handleStatusClick(event, task)}
        />
      );
    }
  };

  const renderPriority = (severity, task) => {
    const commonBoxStyles = {
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      height: "100%",
      padding: "4px 0",
    };

    if (severity === "Highest") {
      return (
        <Box
          sx={{ ...commonBoxStyles, color: "error.main" }}
          onClick={(event) => handlePriorityClick(event, task)}
        >
          <HighestIcon style={{ width: 20, height: 20, marginRight: 8 }} />
          <Typography variant="body2" color="error">
            {t("Highest")}
          </Typography>
        </Box>
      );
    } else if (severity === "High") {
      return (
        <Box
          sx={{ ...commonBoxStyles, color: "warning.main" }}
          onClick={(event) => handlePriorityClick(event, task)}
        >
          <HighIcon style={{ width: 20, height: 20, marginRight: 8 }} />
          <Typography variant="body2" color="warning.dark">
            {t("High")}
          </Typography>
        </Box>
      );
    } else if (severity === "Medium") {
      return (
        <Box
          sx={{ ...commonBoxStyles, color: "info.main" }}
          onClick={(event) => handlePriorityClick(event, task)}
        >
          <MediumIcon style={{ width: 20, height: 20, marginRight: 8 }} />
          <Typography variant="body2" color="info.main">
            {t("Medium")}
          </Typography>
        </Box>
      );
    } else if (severity === "Low") {
      return (
        <Box
          sx={{ ...commonBoxStyles, color: "success.main" }}
          onClick={(event) => handlePriorityClick(event, task)}
        >
          <LowIcon style={{ width: 20, height: 20, marginRight: 8 }} />
          <Typography variant="body2" color="success.main">
            {t("Low")}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const renderAssignee = (assignees, task) => {
    if (assignees && assignees.length > 0) {
      return (
        <Box
          sx={{
            cursor: "pointer",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            height: "100%",
          }}
          onClick={(event) => handleAssigneeClick(event, task)}
        >
          <AvatarGroup
            max={3}
            spacing="medium"
            sx={{
              "& .MuiAvatar-root": {
                width: 24,
                height: 24,
                fontSize: 12,
              },
            }}
          >
            {assignees.map((user, index) => (
              <Tooltip key={user._id} title={user.fullName}>
                <Avatar
                  sx={{ bgcolor: colorPalette[index % colorPalette.length] }}
                >
                  {user.fullName?.charAt(0)}
                </Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            color: "text.secondary",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            height: "100%",
          }}
          onClick={(event) => handleAssigneeClick(event, task)}
        >
          <Person fontSize="small" />
          <Typography variant="body2">{t("Unassigned")}</Typography>
        </Box>
      );
    }
  };

  const renderDueDate = (dueDate, task) => {
    if (dueDate) {
      return (
        <Box
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
          onClick={(event) => handleDateClick(event, task)}
        >
          <Typography variant="body2">
            {dueDate &&
              moment(dueDate, "YYYY-MM-DDTHH:mm:ss:SSS")?.isValid() &&
              moment(dueDate, "YYYY-MM-DDTHH:mm:ss:SSS")?.format(
                dateFormat || "DD MMM, YYYY"
              )}
          </Typography>
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            color: "text.secondary",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            height: "100%",
            gap: 0.5,
            cursor: "pointer",
          }}
          onClick={(event) => handleDateClick(event, task)}
        >
          <CalendarToday fontSize="small" />
          <Typography variant="body2">{t("Set date")}</Typography>
        </Box>
      );
    }
  };

  const renderTags = (tags, task) => {
    const validTags = Array.isArray(tags) ? tags.filter((tag) => tag) : [];

    if (validTags.length > 0) {
      return (
        <Box
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            height: "100%",
            flexWrap: "wrap",
          }}
          onClick={(event) => handleTagsClick(event, task)}
        >
          {validTags.slice(0, 2).map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              sx={{
                backgroundColor: tag.color || "#e0e0e0",
                color: theme.palette.getContrastText(tag.color || "#e0e0e0"),
                fontSize: "12px",
                height: "20px",
                "& .MuiChip-label": {
                  px: 1,
                },
              }}
            />
          ))}
          {validTags.length > 2 && (
            <Typography variant="caption" color="text.secondary">
              +{validTags.length - 2}
            </Typography>
          )}
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            color: "text.secondary",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            height: "100%",
          }}
          onClick={(event) => handleTagsClick(event, task)}
        >
          <LocalOffer fontSize="small" />
          <Typography variant="body2">{t("Add tags")}</Typography>
        </Box>
      );
    }
  };

  const renderComments = (commentCount, taskId, taskName) => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          height: "100%",
          justifyContent: "center",
        }}
        onClick={(event) => handleViewComments(event, taskId, taskName)}
      >
        <ChatBubbleOutline sx={{ fontSize: 16, color: "text.secondary" }} />
        <Typography variant="body2" sx={{ lineHeight: 1 }}>
          {commentCount || "0"}
        </Typography>
      </Box>
    );
  };

  // Get all tasks for selection (grouped + non-grouped)
  const getAllTasks = () => {
    if (groupBy === "None") {
      return globalRowsData;
    } else {
      const allTasks = [];
      Object.keys(columns).forEach((groupId) => {
        const groupData = columns[groupId];
        // Apply the same filtering logic here and exclude special rows
        if (
          groupData &&
          !groupData?.hidden &&
          getRowsAsGrouped(groupData) &&
          groupId !== START_SPRINT_ROW_ID
        ) {
          const tasks = groupData?.tasks || [];
          allTasks.push(...convertTasksToRows(tasks));
        }
      });
      return allTasks;
    }
  };

  const allTasks = getAllTasks();

  // Helper function to get all task IDs in a group
  const getGroupTaskIds = (groupId: string): string[] => {
    const groupData = columns[groupId];
    if (!groupData || !groupData.tasks) return [];
    return groupData.tasks.map((task) => task._id);
  };

  // Check if all tasks in a group are selected
  const isGroupFullySelected = (groupId: string): boolean => {
    const groupTaskIds = getGroupTaskIds(groupId);
    if (groupTaskIds.length === 0) return false;
    return groupTaskIds.every((taskId) => selectedTasks.includes(taskId));
  };

  // Check if some (but not all) tasks in a group are selected
  const isGroupPartiallySelected = (groupId: string): boolean => {
    const groupTaskIds = getGroupTaskIds(groupId);
    if (groupTaskIds.length === 0) return false;
    const selectedCount = groupTaskIds.filter((taskId) =>
      selectedTasks.includes(taskId)
    ).length;
    return selectedCount > 0 && selectedCount < groupTaskIds.length;
  };

  // Handle group checkbox change
  const handleGroupCheckboxChange = (groupId: string, checked: boolean) => {
    const groupTaskIds = getGroupTaskIds(groupId);

    if (checked) {
      // Add all group tasks to selection
      setSelectedTasks((prev) => {
        const newSelection = new Set([...prev, ...groupTaskIds]);
        return Array.from(newSelection);
      });
    } else {
      // Remove all group tasks from selection
      setSelectedTasks((prev) =>
        prev.filter((taskId) => !groupTaskIds.includes(taskId))
      );
    }
  };

  // Filter users for assignee dropdown
  const filteredUsers =
    users?.data?.decryptedData?.filter((user) =>
      user.fullName.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

  // Filter tags for tags dropdown
  const filteredTags = availableTags.filter((tag) =>
    tag.tagName.toLowerCase().includes(tagSearchQuery.toLowerCase())
  );

  // Find selected task for modal
  const selectedTask = findTask(selectedTaskId);
  const taskToDeleteName = findTask(taskToDelete)?.summary || "";
  const taskToDeleteType = findTask(taskToDelete)?.type || "";

  const getLoadingStatus = () => {
    const keys = Object.keys(loadingStates.loadingStatuses);
    let isLoading = isDeleting || isUpdating || isUpdatingMultipleBugs;
    keys?.forEach((key) => {
      if (
        loadingStates.loadingStatuses?.[key]?.isInitialLoading ||
        loadingStates.loadingStatuses?.[key]?.isLoadingMore
      ) {
        isLoading = true;
      }
    });
    return isLoading;
  };

  // Define grid columns for non-grouped view
  const gridColumns = [
    {
      field: "summary",
      headerName: t("Name"),
      flex: 2,
      editable: true,
      renderCell: (params) => {
        const originalTask = findTask(params.row._id);
        const type = originalTask?.type || "";

        const typeInfo = type
          ? typeToIconMapping[type]
          : typeToIconMapping.TASK;

        const displayText = originalTask?.bugKey
          ? `${originalTask.bugKey}: ${params.value}`
          : params.value;

        return (
          <Tooltip
            title={displayText}
            placement="top-start"
            arrow
            enterDelay={500}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
                height: "100%",
                cursor: "pointer",
              }}
              onClick={(e) => {
                handleEditTask(params.row._id);
              }}
            >
              {type && (
                <Box
                  component="span"
                  sx={{
                    width: 16,
                    height: 16,
                    mr: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: typeInfo.bgcolor,
                    borderRadius: "3px",
                    color: "white",
                  }}
                >
                  {typeInfo.icon}
                </Box>
              )}
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {originalTask?.bugKey && (
                  <span style={{ marginRight: "4px", color: "#1976D2" }}>
                    {originalTask.bugKey}:
                  </span>
                )}
                {params.value}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "assignTo",
      headerName: t("Assignee"),
      flex: 1,
      renderCell: (params) => {
        const task = findTask(params.id);
        return renderAssignee(params.value, task);
      },
      sortComparator: (v1, v2) => {
        if (!v1 || v1.length === 0) {
          if (!v2 || v2.length === 0) return 0;
          return 1;
        }
        if (!v2 || v2.length === 0) return -1;

        const name1 = v1[0]?.fullName?.toLowerCase() || "";
        const name2 = v2[0]?.fullName?.toLowerCase() || "";

        return name1.localeCompare(name2);
      },
    },
    {
      field: "bugStatus",
      headerName: t("Status"),
      flex: 1,
      renderCell: (params) => {
        const task = findTask(params.id);
        return renderStatus(params.value, task);
      },
    },
    {
      field: "dueDate",
      headerName: t("Due date"),
      flex: 1,
      renderCell: (params) => {
        const task = findTask(params.id);
        return renderDueDate(params.value, task);
      },
    },
    {
      field: "severity",
      headerName: t("Priority"),
      flex: 1,
      renderCell: (params) => {
        const task = findTask(params.id);
        return renderPriority(params.value, task);
      },
    },
    {
      field: "tags",
      headerName: t("Tags"),
      flex: 1,
      renderCell: (params) => {
        const task = findTask(params.id);
        return renderTags(params.value, task);
      },
    },
    {
      field: "commentCount",
      headerName: t("Comments"),
      width: 100,
      renderCell: (params) => {
        const task = findTask(params.id);
        return renderComments(params.value, params.id, task?.summary || "");
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 50,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(event) => handleActionMenuOpen(event, params.id)}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box
      sx={{
        height: hasFilters ? "calc(100vh - 220px)" : "calc(100vh - 180px)",
        width: "100%",
        position: "relative",
      }}
    >
      {/* Global loading indicator */}
      {isBugsLoading && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress size={60} thickness={4} />
        </Box>
      )}

      {!isBugsLoading && (
        <>
          {/* Case 1: No Grouping */}
          {groupBy === "None" && (
            <>
              {/* Show special row for SCRUM boards with no active sprints */}
              {boardType === "SCRUM" && activeSprintIds?.length === 0 && (
                <Box
                  sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      backgroundColor: "#f8f9fa",
                      borderRadius: 1,
                      px: 2,
                      py: 1,
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ fontWeight: "bold" }}
                      >
                        {t(
                          "Get started in backlog. Plan and start a sprint to see work here."
                        )}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGoToBacklog();
                      }}
                      size="medium"
                      sx={{
                        textTransform: "none",
                        fontSize: "14px",
                        mx: 2,
                      }}
                    >
                      {t("Go to backlog")}
                    </Button>
                  </Box>
                </Box>
              )}
              <DataGridPro
                rows={globalRowsData}
                columns={gridColumns}
                loading={getLoadingStatus()}
                checkboxSelection
                disableRowSelectionOnClick
                onRowSelectionModelChange={handleSelectionModelChange}
                rowSelectionModel={selectedTasks}
                pagination
                paginationMode="server"
                rowCount={globalTotalRows}
                paginationModel={{
                  page: (pagination["global"]?.page || 1) - 1,
                  pageSize: pagination["global"]?.limit || 25,
                }}
                onPaginationModelChange={(model) => {
                  handleGlobalPaginationChange({
                    page: model.page + 1,
                    limit: model.pageSize,
                  });
                }}
                pageSizeOptions={[10, 20, 25, 50, 100]}
                getRowClassName={(params) =>
                  selectedTasks.includes(params.id) ? "selected-row" : ""
                }
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={(error) => {
                  console.error("Error processing row update:", error);
                }}
                //@ts-ignore
                experimentalFeatures={{ newEditingApi: true }}
                disableColumnMenu
                disableVirtualization={false}
                slots={{
                  pagination: () => (
                    <Box
                      sx={{ borderTop: "1px solid", borderColor: "divider" }}
                    >
                      <Pagination
                        total={globalTotalRows}
                        paginationModel={{
                          page: pagination["global"]?.page || 1,
                          pageSize: pagination["global"]?.limit || 25,
                        }}
                        setPaginationModel={(newModel) =>
                          handleGlobalPaginationChange(newModel)
                        }
                        //@ts-ignore
                        showRecordsInfo={true}
                      />
                    </Box>
                  ),
                }}
                sx={{
                  "& .selected-row": {
                    backgroundColor: "rgba(51, 153, 255, 0.05)",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-cell--editing": {
                    boxShadow: "none !important",
                    outline: "none !important",
                    padding: "0 !important",
                    backgroundColor: "transparent !important",
                  },
                  border: "none",
                }}
              />
            </>
          )}

          {/* Case 2: Grouped View */}
          {groupBy !== "None" && (
            <TableContainer
              component={Paper}
              sx={{ height: "100%", overflow: "auto", position: "relative" }}
            >
              {/* Loading overlay for grouped view */}
              {getLoadingStatus() && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1000,
                  }}
                >
                  <CircularProgress size={60} />
                </Box>
              )}
              <Table stickyHeader>
                {/* Common Table Header with Sorting */}
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedTasks.length > 0 &&
                          selectedTasks.length < allTasks.length
                        }
                        checked={
                          allTasks.length > 0 &&
                          selectedTasks.length === allTasks.length
                        }
                        onChange={(event) => {
                          if (event.target.checked) {
                            setSelectedTasks(allTasks.map((task) => task.id));
                          } else {
                            setSelectedTasks([]);
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortBy === "summary"}
                        direction={sortBy === "summary" ? sortDirection : "asc"}
                        onClick={() => handleSort("summary")}
                      >
                        {t("Name")}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortBy === "assignTo"}
                        direction={
                          sortBy === "assignTo" ? sortDirection : "asc"
                        }
                        onClick={() => handleSort("assignTo")}
                      >
                        {t("Assignee")}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortBy === "bugStatus"}
                        direction={
                          sortBy === "bugStatus" ? sortDirection : "asc"
                        }
                        onClick={() => handleSort("bugStatus")}
                      >
                        {t("Status")}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortBy === "dueDate"}
                        direction={sortBy === "dueDate" ? sortDirection : "asc"}
                        onClick={() => handleSort("dueDate")}
                      >
                        {t("Due date")}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortBy === "severity"}
                        direction={
                          sortBy === "severity" ? sortDirection : "asc"
                        }
                        onClick={() => handleSort("severity")}
                      >
                        {t("Priority")}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t("Tags")}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("Comments")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}></TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {/* Add Start Sprint Row if conditions are met */}
                  {boardType === "SCRUM" && activeSprintIds?.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        sx={{
                          py: 2,
                          px: 3,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            backgroundColor: "#f8f9fa",
                            borderRadius: 1,
                            px: 2,
                            py: 1,
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="body1"
                              color="text.secondary"
                              sx={{ fontWeight: "bold" }}
                            >
                              {t(
                                "Get started in backlog. Plan and start a sprint to see work here."
                              )}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            size="medium"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGoToBacklog();
                            }}
                            sx={{
                              textTransform: "none",
                              fontSize: "14px",
                              mx: 2,
                            }}
                          >
                            {t("Go to backlog")}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  {columnOrder
                    .map((groupId) => {
                      const groupData = columns[groupId];
                      return groupData;
                    })
                    .filter((groupData) => {
                      // Apply the filtering logic here
                      return (
                        groupData &&
                        !groupData?.hidden &&
                        getRowsAsGrouped(groupData)
                      );
                    })
                    .map((groupData) => {
                      const groupId = groupData.id;
                      const totalCount = groupData?.totalCount || 0;
                      const isExpanded = expandedGroups[groupId] || false;
                      const tasks = groupData?.tasks || [];
                      const isGroupLoading =
                        loadingStates.loadingStatuses[groupId]
                          ?.isInitialLoading || false;

                      return (
                        <React.Fragment key={groupId}>
                          {/* Group Header Row */}
                          <TableRow
                            sx={{
                              backgroundColor: "background.default",
                              "&:hover": {
                                backgroundColor: "action.hover",
                              },
                            }}
                          >
                            <TableCell
                              colSpan={9}
                              sx={{
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                py: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {/* Group Checkbox */}
                                <Checkbox
                                  checked={isGroupFullySelected(groupId)}
                                  indeterminate={isGroupPartiallySelected(
                                    groupId
                                  )}
                                  onChange={(event) => {
                                    event.stopPropagation();
                                    handleGroupCheckboxChange(
                                      groupId,
                                      event.target.checked
                                    );
                                  }}
                                  onClick={(event) => event.stopPropagation()}
                                  sx={{ p: 0.5 }}
                                />

                                {/* Expand/Collapse Icon */}
                                <IconButton
                                  size="small"
                                  onClick={() => handleGroupToggle(groupId)}
                                >
                                  {isExpanded ? (
                                    <KeyboardArrowDown />
                                  ) : (
                                    <KeyboardArrowRight />
                                  )}
                                </IconButton>

                                {/* Group Status/Icon */}
                                {groupData?.type === "status" ? (
                                  <>
                                    {groupData?.icon && (
                                      <StatusIcon
                                        iconName={groupData?.icon}
                                        size={20}
                                      />
                                    )}
                                  </>
                                ) : (
                                  <Box
                                    sx={{
                                      bgcolor:
                                        groupBy === "Status"
                                          ? groupData.color
                                          : "transparent",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      borderRadius: "50%",
                                      p: 0.2,
                                    }}
                                  >
                                    <Icon
                                      icon={groupData.icon}
                                      width={20}
                                      height={20}
                                      style={{
                                        color:
                                          groupBy === "Status"
                                            ? "text.secondary"
                                            : groupData.color || "#666",
                                      }}
                                    />
                                  </Box>
                                )}

                                {/* Group Title */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    cursor: "pointer",
                                    flex: 1,
                                  }}
                                  onClick={() => handleGroupToggle(groupId)}
                                >
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {groupData?.title || groupId}
                                  </Typography>

                                  {/* Count */}
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "text.secondary" }}
                                  >
                                    {totalCount}
                                  </Typography>
                                </Box>

                                {/* Loading Indicator for specific group */}
                                {isGroupLoading && (
                                  <CircularProgress
                                    size={16}
                                    thickness={4}
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* Collapsed Group Tasks */}
                          {isExpanded && (
                            <React.Fragment>
                              {tasks.map((task, index) => {
                                const taskRow = convertTasksToRows([task])[0];
                                return (
                                  <TableRow
                                    key={task._id}
                                    sx={{
                                      "&:hover": {
                                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                                      },
                                      ...(selectedTasks.includes(task._id) && {
                                        backgroundColor:
                                          "rgba(51, 153, 255, 0.05)",
                                      }),
                                    }}
                                  >
                                    <TableCell padding="checkbox">
                                      <Checkbox
                                        checked={selectedTasks.includes(
                                          task._id
                                        )}
                                        onChange={(event) => {
                                          if (event.target.checked) {
                                            setSelectedTasks((prev) => [
                                              ...prev,
                                              task._id,
                                            ]);
                                          } else {
                                            setSelectedTasks((prev) =>
                                              prev.filter(
                                                (id) => id !== task._id
                                              )
                                            );
                                          }
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          pl: 4,
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            color: "text.secondary",
                                            mr: 1,
                                          }}
                                        >
                                          {index + 1}
                                        </Typography>

                                        {task.type && (
                                          <Box
                                            component="span"
                                            sx={{
                                              width: 16,
                                              height: 16,
                                              mr: 1,
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              backgroundColor:
                                                typeToIconMapping[task.type]
                                                  ?.bgcolor || "#ccc",
                                              borderRadius: "3px",
                                              color: "white",
                                            }}
                                          >
                                            {typeToIconMapping[task.type]
                                              ?.icon || "T"}
                                          </Box>
                                        )}

                                        <Typography variant="body2">
                                          {task.bugKey && (
                                            <span
                                              style={{
                                                marginRight: "4px",
                                                color: "#1976D2",
                                              }}
                                            >
                                              {task.bugKey}:
                                            </span>
                                          )}
                                          {task.summary}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                      {renderAssignee(task.assignTo, taskRow)}
                                    </TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                      {renderStatus(task.bugStatus, taskRow)}
                                    </TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                      {renderDueDate(task.dueDate, taskRow)}
                                    </TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                      {renderPriority(task.severity, taskRow)}
                                    </TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                      {renderTags(task.tags, taskRow)}
                                    </TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                      {renderComments(
                                        task.commentCount,
                                        task._id,
                                        task.summary
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                      <IconButton
                                        size="small"
                                        onClick={(event) =>
                                          handleActionMenuOpen(event, task._id)
                                        }
                                      >
                                        <MoreHoriz fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}

                              {/* Add New Task Row */}
                              {isExpanded && (
                                <TableRow>
                                  <TableCell colSpan={9} sx={{ py: 1, pl: 8 }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        cursor: "pointer",
                                        color: "text.secondary",
                                        "&:hover": {
                                          color: "primary.main",
                                        },
                                      }}
                                      onClick={() => {
                                        dispatch(setCreateTask(true));
                                        if (groupBy === "Status") {
                                          dispatch(
                                            setSelectedColumnStatus(groupId)
                                          );
                                        }
                                      }}
                                    >
                                      <Add fontSize="small" />
                                      <Typography variant="body2">
                                        {t("Add item")}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              )}

                              {/* Group Pagination */}
                              {isExpanded && tasks.length > 0 && (
                                <TableRow>
                                  <TableCell
                                    colSpan={9}
                                    sx={{ borderBottom: "none", p: 1 }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Pagination
                                        total={totalCount}
                                        paginationModel={{
                                          page: pagination[groupId]?.page || 1,
                                          pageSize:
                                            pagination[groupId]?.limit || 25,
                                        }}
                                        setPaginationModel={(newModel) =>
                                          handleGroupPageChange(
                                            groupId,
                                            newModel
                                          )
                                        }
                                        // showRecordsInfo={true}
                                      />
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          )}
                        </React.Fragment>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleActionMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => handleEditTask(actionTaskId)}>
          <Typography variant="body2">{t("Edit")}</Typography>
        </MenuItem>
        <MenuItem onClick={() => openDeleteModal(actionTaskId)}>
          <Typography variant="body2">{t("Delete")}</Typography>
        </MenuItem>
      </Menu>

      {/* Status Dropdown */}
      <Menu
        anchorEl={statusAnchorEl}
        open={Boolean(statusAnchorEl)}
        onClose={() => {
          setStatusAnchorEl(null);
          setIsBulkOperation(false);
          setShowAbove(false);
        }}
        anchorOrigin={
          isBulkOperation
            ? { vertical: "top", horizontal: "center" }
            : showAbove
            ? { vertical: "top", horizontal: "center" }
            : { vertical: "bottom", horizontal: "center" }
        }
        transformOrigin={
          isBulkOperation
            ? { vertical: "bottom", horizontal: "center" }
            : showAbove
            ? { vertical: "bottom", horizontal: "center" }
            : { vertical: "top", horizontal: "center" }
        }
        sx={isBulkOperation || showAbove ? { marginBottom: "8px" } : {}}
      >
        {availableStatus.map((status) => {
          return (
            <MenuItem
              key={status._id}
              onClick={() => {
                if (isBulkOperation) {
                  applyBulkStatusChange(status.status);
                } else if (currentTask) {
                  handleStatusChange(status.status);
                }
              }}
              selected={currentTask?.bugStatus === status.status}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: status.color || "#ccc",
                    flex: "none",
                  }}
                />
                {status.status}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>

      {/* Assignee Dropdown */}
      <Popover
        anchorEl={assigneeAnchorEl}
        open={Boolean(assigneeAnchorEl)}
        onClose={handleAssigneePopoverClose}
        anchorOrigin={
          isBulkOperation
            ? { vertical: "top", horizontal: "center" }
            : showAbove
            ? { vertical: "top", horizontal: "center" }
            : { vertical: "bottom", horizontal: "center" }
        }
        transformOrigin={
          isBulkOperation
            ? { vertical: "bottom", horizontal: "center" }
            : showAbove
            ? { vertical: "bottom", horizontal: "center" }
            : { vertical: "top", horizontal: "center" }
        }
        PaperProps={{
          sx: {
            width: 300,
            maxHeight: 400,
            ...(isBulkOperation || showAbove ? { marginBottom: "8px" } : {}),
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            backgroundColor: "background.paper",
            p: 2,
            pb: 1,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: "#f5f5f5",
              borderRadius: 1,
              p: 0.5,
              mb: 1,
            }}
          >
            <Search sx={{ color: "text.secondary", ml: 1, mr: 1 }} />
            <InputBase
              placeholder={t("Search people...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ ml: 1, flex: 1 }}
            />
            {searchQuery && (
              <IconButton size="small" onClick={() => setSearchQuery("")}>
                <Clear fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box sx={{ overflowY: "auto", p: 2, pt: 0, flexGrow: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {isBulkOperation
              ? t("Assign to all selected tasks")
              : t("Assigned")}
          </Typography>
          {isLoadingUsers ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={24} thickness={4} />
            </Box>
          ) : tempAssignees.length > 0 ? (
            tempAssignees.map((user) => (
              <MenuItem
                key={user._id}
                onClick={() => handleTempAssigneeChange(user._id)}
                sx={{ borderRadius: 1 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      overflow: "hidden",
                      flexGrow: 1,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        mr: 1,
                        bgcolor: "primary.main",
                        flexShrink: 0,
                      }}
                    >
                      {user.fullName?.charAt(0)}
                    </Avatar>
                    <Tooltip title={user.fullName} placement="top">
                      <Typography
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {user.fullName}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Checkbox
                    checked={true}
                    size="small"
                    sx={{ ml: 1, flexShrink: 0 }}
                  />
                </Box>
              </MenuItem>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
              {t("No users assigned")}
            </Typography>
          )}

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("Suggestions")}
          </Typography>

          {filteredUsers.map((user) => {
            if (tempAssignees.some((u) => u._id === user._id)) return null;

            return (
              <MenuItem
                key={user._id}
                onClick={() => handleTempAssigneeChange(user._id)}
                sx={{ borderRadius: 1 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      overflow: "hidden",
                      flexGrow: 1,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        mr: 1,
                        bgcolor: "primary.main",
                        flexShrink: 0,
                      }}
                    >
                      {user.fullName?.charAt(0)}
                    </Avatar>
                    <Tooltip title={user.fullName} placement="top">
                      <Typography
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {user.fullName}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Checkbox
                    checked={false}
                    size="small"
                    sx={{ ml: 1, flexShrink: 0 }}
                  />
                </Box>
              </MenuItem>
            );
          })}
        </Box>
      </Popover>

      {/* Priority Dropdown */}
      <Menu
        anchorEl={priorityAnchorEl}
        open={Boolean(priorityAnchorEl)}
        onClose={() => {
          setPriorityAnchorEl(null);
          setIsBulkOperation(false);
          setShowAbove(false);
        }}
        anchorOrigin={
          isBulkOperation
            ? { vertical: "top", horizontal: "center" }
            : showAbove
            ? { vertical: "top", horizontal: "center" }
            : { vertical: "bottom", horizontal: "center" }
        }
        transformOrigin={
          isBulkOperation
            ? { vertical: "bottom", horizontal: "center" }
            : showAbove
            ? { vertical: "bottom", horizontal: "center" }
            : { vertical: "top", horizontal: "center" }
        }
        sx={isBulkOperation || showAbove ? { marginBottom: "8px" } : {}}
      >
        <MenuItem
          onClick={() => {
            if (isBulkOperation) {
              applyBulkPriorityChange("Highest");
            } else if (currentTask) {
              handlePriorityChange("Highest");
            }
          }}
          selected={currentTask?.severity === "Highest"}
        >
          <ListItemIcon>
            <HighestIcon style={{ width: 20, height: 20 }} />
          </ListItemIcon>
          <Typography variant="body2" color="error">
            {t("Highest")}
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (isBulkOperation) {
              applyBulkPriorityChange("High");
            } else if (currentTask) {
              handlePriorityChange("High");
            }
          }}
          selected={currentTask?.severity === "High"}
        >
          <ListItemIcon>
            <HighIcon style={{ width: 20, height: 20 }} />
          </ListItemIcon>
          <Typography variant="body2" color="warning.dark">
            {t("High")}
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (isBulkOperation) {
              applyBulkPriorityChange("Medium");
            } else if (currentTask) {
              handlePriorityChange("Medium");
            }
          }}
          selected={currentTask?.severity === "Medium"}
        >
          <ListItemIcon>
            <MediumIcon style={{ width: 20, height: 20 }} />
          </ListItemIcon>
          <Typography variant="body2" color="info.main">
            {t("Medium")}
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (isBulkOperation) {
              applyBulkPriorityChange("Low");
            } else if (currentTask) {
              handlePriorityChange("Low");
            }
          }}
          selected={currentTask?.severity === "Low"}
        >
          <ListItemIcon>
            <LowIcon style={{ width: 20, height: 20 }} />
          </ListItemIcon>
          <Typography variant="body2" color="success.main">
            {t("Low")}
          </Typography>
        </MenuItem>
      </Menu>

      {/* Tags Dropdown */}
      <Popover
        anchorEl={tagsAnchorEl}
        open={Boolean(tagsAnchorEl)}
        onClose={handleTagsPopoverClose}
        anchorOrigin={
          isBulkOperation
            ? { vertical: "top", horizontal: "center" }
            : showAbove
            ? { vertical: "top", horizontal: "center" }
            : { vertical: "bottom", horizontal: "center" }
        }
        transformOrigin={
          isBulkOperation
            ? { vertical: "bottom", horizontal: "center" }
            : showAbove
            ? { vertical: "bottom", horizontal: "center" }
            : { vertical: "top", horizontal: "center" }
        }
        PaperProps={{
          sx: {
            width: 300,
            maxHeight: 400,
            ...(isBulkOperation || showAbove ? { marginBottom: "8px" } : {}),
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            backgroundColor: "background.paper",
            p: 2,
            pb: 1,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: "#f5f5f5",
              borderRadius: 1,
              p: 0.5,
              mb: 1,
            }}
          >
            <Search sx={{ color: "text.secondary", ml: 1, mr: 1 }} />
            <InputBase
              placeholder={t("Search tags...")}
              value={tagSearchQuery}
              onChange={(e) => setTagSearchQuery(e.target.value)}
              sx={{ ml: 1, flex: 1 }}
            />
            {tagSearchQuery && (
              <IconButton size="small" onClick={() => setTagSearchQuery("")}>
                <Clear fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box sx={{ overflowY: "auto", p: 2, pt: 0, flexGrow: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {isBulkOperation
              ? t("Tags for all selected tasks")
              : t("Selected Tags")}
          </Typography>

          {tempTags.length > 0 ? (
            tempTags.map((tag) => (
              <MenuItem
                key={tag._id}
                onClick={() => handleTempTagChange(tag._id)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      overflow: "hidden",
                      flexGrow: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        bgcolor: tag.color || "#e0e0e0",
                        mr: 1,
                        flexShrink: 0,
                      }}
                    />
                    <Tooltip title={tag.tagName} placement="top">
                      <Typography
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {tag.tagName}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Checkbox
                    checked={true}
                    size="small"
                    sx={{ ml: 1, flexShrink: 0 }}
                  />
                </Box>
              </MenuItem>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
              {t("No tags selected")}
            </Typography>
          )}

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("Available Tags")}
          </Typography>

          {filteredTags.length > 0 ? (
            filteredTags.map((tag) => {
              if (tempTags.some((t) => t._id === tag._id)) return null;

              return (
                <MenuItem
                  key={tag._id}
                  onClick={() => handleTempTagChange(tag._id)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        overflow: "hidden",
                        flexGrow: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          bgcolor: tag.color || "#e0e0e0",
                          mr: 1,
                          flexShrink: 0,
                        }}
                      />
                      <Tooltip title={tag.tagName} placement="top">
                        <Typography
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {tag.tagName}
                        </Typography>
                      </Tooltip>
                    </Box>
                    <Checkbox
                      checked={false}
                      size="small"
                      sx={{ ml: 1, flexShrink: 0 }}
                    />
                  </Box>
                </MenuItem>
              );
            })
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
              {tagSearchQuery
                ? t("No tags found matching your search")
                : t("No tags available")}
            </Typography>
          )}
        </Box>
      </Popover>
      {/* Project Dropdown Popover */}
      <Popover
        anchorEl={projectAnchorEl}
        open={Boolean(projectAnchorEl)}
        onClose={() => {
          setProjectAnchorEl(null);
          setIsBulkOperation(false);
          setProjectSearchQuery("");
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            width: 300,
            maxHeight: 400,
            marginBottom: "8px",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Search Box */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            backgroundColor: "background.paper",
            p: 2,
            pb: 1,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: "#f5f5f5",
              borderRadius: 1,
              p: 0.5,
              mb: 1,
            }}
          >
            <Search sx={{ color: "text.secondary", ml: 1, mr: 1 }} />
            <InputBase
              placeholder={t("Search projects...")}
              value={projectSearchQuery}
              onChange={(e) => setProjectSearchQuery(e.target.value)}
              sx={{ ml: 1, flex: 1 }}
            />
            {projectSearchQuery && (
              <IconButton
                size="small"
                onClick={() => setProjectSearchQuery("")}
              >
                <Clear fontSize="small" />
              </IconButton>
            )}
          </Box>
          {allProjectsWithoutJiraConfg
            ?.filter((project) =>
              project.name
                .toLowerCase()
                .includes(projectSearchQuery.toLowerCase())
            )
            .filter((project) => project._id !== id).length === 0 && ( // ADDED: Filter out current project
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
              {projectSearchQuery
                ? t("No projects found")
                : t("No projects available")}
            </Typography>
          )}
          {allProjectsWithoutJiraConfg
            ?.filter((project) =>
              project.name
                .toLowerCase()
                .includes(projectSearchQuery.toLowerCase())
            )
            .filter((project) => project._id !== id)
            .map((project) => (
              <MenuItem
                key={project._id}
                onClick={(e) => handleProjectClick(e, project._id)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      overflow: "hidden",
                      flexGrow: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        bgcolor: project.icon || "#3399ff",
                        mr: 1,
                        flexShrink: 0,
                      }}
                    />
                    <Tooltip title={project.name} placement="top">
                      <Typography
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {project.name}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <KeyboardArrowRight
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                </Box>
              </MenuItem>
            ))}
        </Box>
      </Popover>

      {/* Status Selection Sub-Menu for Selected Project */}
      <Popover
        anchorEl={statusAnchorElForProject}
        open={
          Boolean(statusAnchorElForProject) && Boolean(selectedProjectForStatus)
        }
        onClose={() => {
          setStatusAnchorElForProject(null);
          setSelectedProjectForStatus(null);
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            width: 250,
            maxHeight: 400,
            ml: 1,
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t("Select Status")}
          </Typography>
        </Box>
        <Box sx={{ overflowY: "auto", p: 1 }}>
          {isStatusLoading || isStatusFetching ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : projectStatuses.length > 0 ? (
            projectStatuses.map((status) => (
              <MenuItem
                key={status._id}
                onClick={() => {
                  if (selectedProjectForStatus) {
                    applyBulkProjectChange(
                      selectedProjectForStatus,
                      status._id
                    );
                  }
                }}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      bgcolor: status.color || "#ccc",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {status.status}
                  </Typography>
                </Box>
              </MenuItem>
            ))
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ p: 2, textAlign: "center" }}
            >
              {t("No statuses available")}
            </Typography>
          )}
        </Box>
      </Popover>

      {/* Date Picker */}
      <DatePickerPopover
        anchorEl={dateAnchorEl}
        onClose={() => {
          setDateAnchorEl(null);
          setIsBulkOperation(false);
          setShowAbove(false);
        }}
        selectedDate={currentTask?.dueDate}
        onDateSelect={handleDateSelect}
        isBulkUpdate={isBulkOperation}
        onBulkDateSelect={applyBulkDateChange}
        isBulkOperation={isBulkOperation}
        showAbove={showAbove}
      />

      {/* Comments Popover */}
      <CommentsPopover
        anchorEl={commentsAnchorEl}
        onClose={handleCloseComments}
        comments={comments}
        taskId={commentTaskId}
        isLoading={isLoadingComments}
        taskName={currentTaskName}
      />

      {/* Task Edit Modal/Drawer */}
      {selectedTaskId && isTaskModalOpen && (
        <TaskUpdateDrawer
          open={isTaskModalOpen}
          onClose={handleCloseTaskModal}
          setBugId={() => {}}
          setDrawerOpen={() => {}}
          bugId={selectedTaskId}
          users={users?.data?.decryptedData || []}
          createdBy={selectedTask?.createdBy?.fullName || ""}
          setCreatedBy={() => {}}
          refreshColumn={() => {
            // Refresh the table data after task update
            refreshData();
          }}
        />
      )}

      {/* Delete Confirmation Modal for Tasks */}
      <DeleteConfirmModal
        open={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteTask}
        loading={isDeleting}
        title={
          Array.isArray(taskToDelete) ? t("Delete Tasks") : t("Delete Task")
        }
        confirmMessage={t("Are you sure you want to delete ?")}
        okText={t("Delete")}
      />

      {/* Task Selection Overlay */}
      <TaskSelectionOverlay
        selectedTasks={selectedTasks}
        onClearSelection={handleClearSelection}
        onStatusChange={handleBulkStatusChange}
        onAssigneesChange={handleBulkAssigneesChange}
        onDatesChange={handleBulkDatesChange}
        onPriorityChange={handleBulkPriorityChange}
        onTagsChange={handleBulkTagsChange}
        onProjectChange={handleBulkProjectChange}
        onCustomFieldsChange={(_anchorEl) => {}}
        onMoveAdd={() => {}}
        onConvertToTask={() => {}}
        onCopy={() => {}}
        onDelete={handleBulkDelete}
      />
    </Box>
  );
};

export default BoardTableView;
